import { useEffect, useState } from "react";
import { Shield, Search, ChevronDown, ChevronUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { getSupabaseClient } from "../providers/supabase/supabase";

type AuditRow = {
  id: number;
  table_name: string;
  record_id: number | null;
  operation: string;
  changed_by_name: string;
  old_values: Record<string, any> | null;
  new_values: Record<string, any> | null;
  created_at: string;
};

const OP_BADGE: Record<string, string> = {
  INSERT: "bg-green-100 text-green-700",
  UPDATE: "bg-blue-100 text-blue-700",
  DELETE: "bg-red-100 text-red-700",
};

const TABLE_NAMES = ["orders", "quotations", "order_payments", "contacts"];

const JsonDiff = ({ old: o, n }: { old: Record<string, any> | null; n: Record<string, any> | null }) => {
  const [open, setOpen] = useState(false);
  const changedKeys = n && o ? Object.keys(n).filter(k => JSON.stringify(n[k]) !== JSON.stringify(o[k])) : [];
  if (!n && !o) return null;

  return (
    <div>
      <button onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {changedKeys.length > 0 ? `${changedKeys.length} 个字段变更` : "查看详情"}
      </button>
      {open && (
        <div className="mt-1.5 space-y-1">
          {changedKeys.length > 0 ? changedKeys.map(k => (
            <div key={k} className="grid grid-cols-[100px_1fr_1fr] gap-2 text-xs bg-muted/50 rounded px-2 py-1">
              <span className="font-mono text-muted-foreground truncate">{k}</span>
              <span className="text-red-600 truncate">{JSON.stringify(o?.[k])}</span>
              <span className="text-green-600 truncate">{JSON.stringify(n?.[k])}</span>
            </div>
          )) : (
            <pre className="text-xs bg-muted/50 rounded p-2 overflow-x-auto max-h-32">
              {JSON.stringify(n ?? o, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

export const AuditLogPage = () => {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tableFilter, setTableFilter] = useState("all");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data } = await getSupabaseClient()
        .from("audit_logs")
        .select(`
          id, table_name, record_id, operation, old_values, new_values, created_at,
          sales ( first_name, last_name )
        `)
        .order("created_at", { ascending: false })
        .limit(200);

      setRows((data ?? []).map((r: any) => ({
        id: r.id,
        table_name: r.table_name,
        record_id: r.record_id,
        operation: r.operation,
        changed_by_name: r.sales ? `${r.sales.first_name} ${r.sales.last_name}` : "系统",
        old_values: r.old_values,
        new_values: r.new_values,
        created_at: r.created_at,
      })));
      setLoading(false);
    };
    load();
  }, []);

  const filtered = rows.filter(r => {
    if (tableFilter !== "all" && r.table_name !== tableFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return r.table_name.includes(q)
        || String(r.record_id ?? "").includes(q)
        || r.changed_by_name.toLowerCase().includes(q)
        || r.operation.toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div className="p-6 space-y-5 max-w-5xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Shield className="w-6 h-6" /> 操作审计日志
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">记录订单、报价单、收款的所有增删改操作</p>
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-muted-foreground" />
          <Input className="pl-8 h-8 text-sm" placeholder="搜索..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="flex border rounded-lg overflow-hidden">
          {["all", ...TABLE_NAMES].map(t => (
            <button key={t} onClick={() => setTableFilter(t)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${tableFilter === t ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              {t === "all" ? "全部" : t}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">{[1,2,3,4,5].map(i => <div key={i} className="h-12 bg-muted animate-pulse rounded" />)}</div>
      ) : (
        <div className="bg-card border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 text-xs text-muted-foreground uppercase">
                <th className="text-left px-4 py-2.5">时间</th>
                <th className="text-left px-4 py-2.5">表 / 记录</th>
                <th className="text-center px-4 py-2.5">操作</th>
                <th className="text-left px-4 py-2.5 hidden md:table-cell">操作人</th>
                <th className="text-left px-4 py-2.5">变更详情</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-12 text-muted-foreground text-sm">暂无审计记录</td></tr>
              ) : filtered.map(r => (
                <tr key={r.id} className="border-t hover:bg-muted/20">
                  <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                    {new Date(r.created_at).toLocaleString("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-mono text-xs">{r.table_name}</div>
                    {r.record_id && <div className="text-xs text-muted-foreground">#{r.record_id}</div>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${OP_BADGE[r.operation] ?? "bg-muted"}`}>
                      {r.operation}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs hidden md:table-cell">{r.changed_by_name}</td>
                  <td className="px-4 py-3">
                    <JsonDiff old={r.old_values} n={r.new_values} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

AuditLogPage.path = "/audit-log";
